import { z } from 'zod'

import { TaxIdType } from '@/types/enums'

// Re-export cn from lib/utils to avoid duplication
export { cn } from '@/lib/utils'

function _upsert(array: any[], element: any) {
  // (1)
  const i = array.findIndex((_element) => _element.id === element.id)
  if (i > -1)
    array[i] = element // (2)
  else array.push(element)
}

function _percentage(partialValue: any, totalValue: any) {
  return (totalValue / 100) * partialValue
}

export function currencyFormat(amount: string | number) {
  return new Intl.NumberFormat('USD', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount))
}

export function formatNumber(value: number, maximumFractionDigits = 2, minimumFractionDigits = 2) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits,
    minimumFractionDigits,
  })
}

/**
 * Marks the element a layout scrolls in when that is not the window (the
 * onboarding shell's root). `scrollToTop` scrolls it instead of the window.
 */
export const SCROLL_ROOT_ATTRIBUTE = 'data-scroll-root'

/**
 * Back to the top of the page: the layout's marked scroll root when there is
 * one, otherwise the window. Scrolling only the window left each onboarding
 * step at the previous step's offset (of-2 C6).
 *
 * The marked root JUMPS. Its callers change the step right after, and Chromium
 * cancels a smooth scroll when the next step mounts underneath it, so a smooth
 * scroll left the new step at the old offset on staging.
 */
export const scrollToTop = () => {
  const root = document.querySelector<HTMLElement>(`[${SCROLL_ROOT_ATTRIBUTE}]`)
  if (!root) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  root.scrollTop = 0
}

export const isValidUrl = (url: string) => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * A company website as customers type it: `shopify.com` or `https://shopify.com`.
 * A missing scheme means https; only http(s) and a dotted hostname pass.
 */
export const isValidWebsite = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed || /\s/.test(trimmed)) return false
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`)
    return (url.protocol === 'https:' || url.protocol === 'http:') && /\.[a-z]{2,}$/i.test(url.hostname)
  } catch {
    return false
  }
}

export const zodValidateTaxId = (data: any, ctx: any) => {
  // List of common placeholder/dummy values that should be rejected
  const PLACEHOLDER_VALUES = [
    '000-00-0000', // Common placeholder for SSN/ITIN
    '999-99-9999', // Test value
    '123-45-6789', // Sequential placeholder
    '111-11-1111', // Repeated digits
    '222-22-2222', // Repeated digits
    '333-33-3333', // Repeated digits
    '00-0000000', // EIN placeholder
    '99-9999999', // EIN placeholder
  ]

  // Check for placeholder values first (before format validation)
  if (PLACEHOLDER_VALUES.includes(data.taxId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "Please enter a valid Tax ID. Placeholder values (like 000-00-0000) are not accepted. If you don't have a US Tax ID yet, please contact support.",
      path: ['taxId'],
    })
    return // Stop further validation if placeholder detected
  }

  if ([TaxIdType.SSN, TaxIdType.ITIN].includes(data.taxIdType)) {
    const SSN_REGEX = /^\d{3}-\d{2}-\d{4}$/
    if (!SSN_REGEX.test(data.taxId)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid tax ID. Format: ###-##-####', path: ['taxId'] })
    }
  } else if (data.taxIdType === TaxIdType.EIN) {
    const EIN_REGEX = /^\d{2}-\d{7}$/
    if (!EIN_REGEX.test(data.taxId)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid tax ID. Format: ##-#######', path: ['taxId'] })
    }
  } else if (data.taxIdType === TaxIdType.FTIN && data.taxId.length === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'FTIN is required', path: ['taxId'] })
  }
}
